import { useMemo } from 'react'
import type { QuotaInfo } from '@/features/auth/types'

// ── Threshold definitions ──────────────────────────────────────────

export type QuotaSeverity = 'info' | 'warning' | 'critical'

export interface QuotaThreshold {
    id: string
    percent: number
    severity: QuotaSeverity
    title: string
    description: string
    showUpgrade: boolean
}

export const QUOTA_THRESHOLDS: QuotaThreshold[] = [
    {
        id: 'usage_50',
        percent: 50,
        severity: 'info',
        title: 'You\'ve used 50% of your responses',
        description: 'You\'re halfway through your response quota for this billing cycle.',
        showUpgrade: false,
    },
    {
        id: 'usage_80',
        percent: 80,
        severity: 'warning',
        title: 'You\'ve used 80% of your responses',
        description: 'Consider upgrading your plan to avoid hitting the limit.',
        showUpgrade: true,
    },
    {
        id: 'usage_90',
        percent: 90,
        severity: 'warning',
        title: '90% of your response quota used',
        description: 'You\'re running low. Upgrade now to keep collecting responses.',
        showUpgrade: true,
    },
    {
        id: 'usage_95',
        percent: 95,
        severity: 'critical',
        title: 'Almost at your response limit',
        description: 'Only 5% remaining. Surveys will pause once you hit the limit.',
        showUpgrade: true,
    },
    {
        id: 'usage_100',
        percent: 100,
        severity: 'critical',
        title: 'Response limit reached — your surveys are paused',
        description: 'Upgrade to resume collecting responses.',
        showUpgrade: true,
    },
]

// ── Dismissed state (scoped to billing period) ─────────────────────

const STORAGE_KEY = 'compra_quota_dismissed'

interface DismissedState {
    key: string
    dismissed: string[]
}

function getDismissedState(): DismissedState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

export function dismissQuotaBanner(dismissKey: string, thresholdId: string) {
    const state = getDismissedState()

    if (!state || state.key !== dismissKey) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ key: dismissKey, dismissed: [thresholdId] }))
        return
    }

    if (!state.dismissed.includes(thresholdId)) {
        state.dismissed.push(thresholdId)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }
}

function isDismissed(dismissKey: string, thresholdId: string): boolean {
    const state = getDismissedState()
    if (!state || state.key !== dismissKey) return false
    return state.dismissed.includes(thresholdId)
}

// ── Hook ───────────────────────────────────────────────────────────

export interface QuotaStatus {
    activeThreshold: QuotaThreshold | null
    isDismissed: boolean
    usagePercent: number
    used: number
    limit: number
    dismissKey: string | null
}

/**
 * Computes current quota usage status from auth /me quota data.
 */
export const useQuotaNotifications = (quota: QuotaInfo | null | undefined): QuotaStatus => {
    const used = quota?.used ?? 0
    const limit = quota?.limit ?? 0
    const billingCycleStart = quota?.billingCycleStart ?? null

    return useMemo(() => {
        const noData: QuotaStatus = {
            activeThreshold: null,
            isDismissed: false,
            usagePercent: 0,
            used: 0,
            limit: 0,
            dismissKey: null,
        }

        if (limit <= 0) return noData

        const usagePercent = (used / limit) * 100
        const dismissKey = billingCycleStart || `limit_${limit}_used_${used}`

        // Find highest crossed threshold (walk from end)
        let active: QuotaThreshold | null = null
        for (let i = QUOTA_THRESHOLDS.length - 1; i >= 0; i--) {
            if (usagePercent >= QUOTA_THRESHOLDS[i].percent) {
                active = QUOTA_THRESHOLDS[i]
                break
            }
        }

        return {
            activeThreshold: active,
            isDismissed: active ? isDismissed(dismissKey, active.id) : false,
            usagePercent: Math.min(usagePercent, 100),
            used,
            limit,
            dismissKey,
        }
    }, [used, limit, billingCycleStart])
}
